<template>
  <a :href="`https://${host}/${alias}${defaultLang != lang ? '/'+ lang : ''}`" target="_blank" class="text-center position-relative" >
    <div class="">
      <v-avatar size="60" :image="image || undefined" :icon="PlaceholderIcon" color="BW-200"></v-avatar>
    </div>
    <div class="tap-name" v-if="name">
      {{name}}
    </div>
    <div class="tap-link">
      {{host}}/{{alias}}{{defaultLang != lang ? `/${lang}`: ''}}
    </div>
    <transition name="fade-copy">
      <div v-if="showCopy" class="green-copy"
           @touchstart="touchStart"
           @touchmove="touchMove"
           @touchend="touchEnd"
           :style="{ transform: `translate(${offsetX}px, ${offsetY}px)` }">
        {{ $t('Скопировано') }}
      </div>
    </transition>
  </a>
  <div class="btn-group">
    <div class="btn-column">
      <ShareKit :link-to-copy="`https://${host}/${alias}${defaultLang != lang ? '/'+ lang : ''}`">
        <v-tooltip location="bottom start" :disabled="published" :text="!published ? $t('Сначала опубликуйте страницу') : ''">
          <template v-slot:activator="{ props }">
            <div class="btn-item" @click="clickShare($event)" :class="{'cursor-not-allowed': !published}" v-bind="props">
              <ShareIcon class="icon" color="#8D959D"></ShareIcon>
              <div class="two-lines">
                {{ $t('Поделиться ссылкой') }}
              </div>
            </div>
          </template>
        </v-tooltip>
      </ShareKit>
    </div>
    <div class="btn-item" @click="copy()">
      <copy-link class="icon"></copy-link>
      <div class="two-lines">
        {{ $t('Копировать ссылку') }}
      </div>
    </div>
    <div class="btn-item" @click="openPage()">
      <GoToPage class="icon go-to-page-icon"/>
      <div class="two-lines">
        {{ $t('Открыть страницу') }}
      </div>
    </div>
    <div class="btn-item" @click="qrGenerate()">
      <qr-icon class="icon"></qr-icon>
      <div class="two-lines">
        {{ $t('QR-код') }}
      </div>
    </div>
  </div>
  <router-link v-if="published" :to="{path: `/profile/${props.alias}/analytics`, query: $route.query}" class="context-item">
    <analytics class="icon"></analytics>
    {{ $t('Аналитика') }}
  </router-link>
  <div class="context-item" @click="copyPage()">
    <tap-copy class="icon"></tap-copy>
    {{ $t('Сделать копию') }}
  </div>
  <div class="context-item" @click="changeName()">
    <rotate class="icon"></rotate>
    {{ $t('Поменять адрес') }}
  </div>
  <div class="context-item context-item--search-visibility">
    <v-icon :icon="SearchIcon" color="BW-500" size="24" class="icon"/>
    <span class="context-item__text">{{ $t('Показывать в поиске TAP') }}</span>
    <v-switch
        :model-value="isSearchable"
        :loading="searchVisibilityLoading"
        :disabled="searchVisibilityLoading"
        :aria-label="$t('Показывать в поиске TAP')"
        color="primary"
        density="compact"
        hide-details
        inset
        class="search-visibility-switch"
        @update:model-value="changeSearchVisibility"
    />
  </div>
  <div class="context-divider" aria-hidden="true"></div>
  <div class="context-item context-item--danger" @click="deletePage()">
    <trash class="icon"></trash>
    {{ $t('Удалить страницу') }}
  </div>
</template>

<script setup lang="ts">
import PlaceholderIcon from "~/assets/icons/placeholder.vue";
import ShareIcon from "~/assets/icons/share.vue";
import CopyLink from "~/assets/icons/copy-link.vue";
import Trash from "~/assets/icons/trash-red.vue";
import {ref} from "vue";
import {useDialogStore} from "@/stores/dialogStore";
import Delete from "@/components/dialogs/Delete.vue";
import TapCopy from "~/assets/icons/tap-copy.vue";
import Copy from "@/components/dialogs/Copy.vue";
import Rotate from "~/assets/icons/rotate.vue";
import ChangeTap from "@/components/dialogs/ChangeTap.vue";
import QrIcon from "~/assets/icons/qr-icon.vue";
import GoToPage from "~/assets/icons/go-to-page.vue";
import QR from "@/components/dialogs/QR.vue";
import Analytics from "~/assets/icons/analitycs.vue";
import SearchIcon from "~/assets/icons/search.vue";
import {useRouter} from "vue-router";
import {useTapStore} from "@/stores/tapStore.js";

const props = defineProps({
  id: { type: Number },
  alias: { type: String },
  name: { type: String },
  image: { type: String },
  lang: { type: String },
  published: { type: Boolean },
  defaultLang: { type: String },
  searchable: { type: Boolean, default: true },
});

const config = useRuntimeConfig()
const host = config.public.tapLink;
const showCopy = ref(false)
const isSearchable = ref(props.searchable)
const searchVisibilityLoading = ref(false)

let startX = 0
let startY = 0
const offsetX = ref(0)
const offsetY = ref(0)
const router = useRouter()

const clickShare = (e: MouseEvent) => {
  if(!props.published) e.stopPropagation();
}

const dialogStore = useDialogStore()
const tapStore = useTapStore()

async function changeSearchVisibility(value: boolean | null) {
  if(typeof value !== 'boolean' || value === isSearchable.value || searchVisibilityLoading.value) return

  const previousValue = isSearchable.value
  isSearchable.value = value
  searchVisibilityLoading.value = true

  try {
    await tapStore.changeSearchVisibility(props.id, value)
  } catch {
    isSearchable.value = previousValue
  } finally {
    searchVisibilityLoading.value = false
  }
}

function deletePage() {
  dialogStore.open(Delete, { id: props.id })
}

function copyPage() {
  dialogStore.open(Copy, { id: props.id })
}

function changeName() {
  dialogStore.open(ChangeTap, { id: props.id })
}

function copy() {
  navigator.clipboard.writeText(`https://${host}/${props.alias}/${props.lang}`)
  showCopy.value = true

  setTimeout(() => {
    showCopy.value = false
  }, 1500)
}

function openPage() {
  dialogStore.close(true)
  router.replace(`/profile/${props.alias}/edit`)
}

function qrGenerate() {
  dialogStore.open(QR, {
    id: props.id,
    image: props.image,
    name: props.name,
    alias: props.alias,
    lang: props.lang,
    defaultLang: props.defaultLang
  })
}

function touchStart(e: TouchEvent) {
  const t = e.touches[0]
  startX = t.clientX
  startY = t.clientY
}

function touchMove(e: TouchEvent) {
  const t = e.touches[0]
  offsetX.value = t.clientX - startX
  offsetY.value = t.clientY - startY
}

function touchEnd() {
  if (Math.abs(offsetX.value) > 30 || Math.abs(offsetY.value) > 30) {
    showCopy.value = false
  }

  offsetX.value = 0
  offsetY.value = 0
}

</script>

<style scoped lang="sass">
.text-center
  text-decoration: none
.green-copy
  position: absolute
  top: 30px
  left: 0
  background: #05A660D9
  width: 100%
  padding: 8px 16px
  border-radius: 8px
  color: #fff
  font-weight: 500
.tap-name
  color: #363636
  text-decoration: none
  font-size: 16px
  font-weight: 600
  height: 21px
  margin-top: 12px
  margin-bottom: 4px
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap
.tap-link
  text-decoration: none
  font-size: 13px
  color: #8D959D
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap
  margin: 0 auto 12px
.btn-group
  display: grid
  grid-template-columns: repeat(4, minmax(0, 1fr))
  padding: 12px 8px
  text-align: center
  margin-bottom: 24px
.btn-column
  min-width: 0
  .btn-item
    width: 100%
.btn-item
  min-width: 0
  cursor: pointer
  font-size: 12px
  line-height: 1.2em
  display: flex
  flex-direction: column
  align-items: center
  gap: 9px
  &:hover
    .icon
      transform: scale(1.1)
  .icon
    padding: 8px
    background: #F6F7F8
    border-radius: 50px
    width: 40px
    height: 40px
    transition: transform 0.2s ease
    svg
      width: 24px
      height: 24px
  .go-to-page-icon
    padding: 0
    background: transparent


.two-lines
  width: 100%
  min-width: 0
  white-space: normal
  height: calc(1.2em * 2)
  overflow: hidden
.context-item
  display: flex
  align-items: center
  gap: 12px
  height: 40px
  padding: 6px 8px
  margin-bottom: 4px
  border-radius: 8px
  cursor: pointer
  text-decoration: none
  color: #363636
  &:hover
    background: #F6F7F8

.context-item--search-visibility
  cursor: default

.context-divider
  height: 1px
  margin-bottom: 4px
  background: #DEE2E6

.context-item--danger
  color: #B20320

.context-item__text
  flex: 1
  line-height: 20px

.search-visibility-switch
  flex: 0 0 auto

.fade-copy-enter-active
  transition: opacity 0.1s ease

.fade-copy-leave-active
  transition: opacity 0.2s ease

.fade-copy-enter-from,
.fade-copy-leave-to
  opacity: 0


</style>
